import paymentsConst from '../../../constants/payments.constant';
import suppliers from './../../supplier/suppliers';
import tripStatuses from './../trip-statuses';
import {tripStates} from './../trip-routes';

export const STAGE = {
    PENDING_CONFIRMATION: 'PendingConfirmation',
    PENDING_APPROVAL: 'PendingApproval',
    APPROVED: 'Approved',
    DECLINED: 'Declined',
    NOT_SENT: 'NotSent',
    DEADLINE_EXCEEDED: 'DeadlineExceeded',

    AMENDMENT_PENDING_APPROVAL: 'AmendmentPendingApproval',
    AMENDMENT_APPROVED: 'AmendmentApproved',
    AMENDMENT_DECLINED: 'AmendmentDeclined',
    AMENDMENT_DEADLINE_EXCEEDED: 'AmendmentDeadlineExceeded',
    AMENDMENT_COMPLETED: 'AmendmentCompleted',
};

export const STAGE_LABELS = {
    [STAGE.PENDING_APPROVAL]: 'Pending approval',
    [STAGE.PENDING_CONFIRMATION]: 'Pending confirmation',
    [STAGE.APPROVED]: 'Approved',
    [STAGE.DECLINED]: 'Declined',
    [STAGE.DEADLINE_EXCEEDED]: 'Approval deadline is missed',
    [STAGE.AMENDMENT_PENDING_APPROVAL]: 'Amendment pending approval',
    [STAGE.AMENDMENT_APPROVED]: 'Amendment approved',
    [STAGE.AMENDMENT_DECLINED]: 'Amendment declined',
    [STAGE.AMENDMENT_DEADLINE_EXCEEDED]: 'Amendment Approval Missed Deadline',
    [STAGE.AMENDMENT_COMPLETED]: '',
};

export default function TripDetailsService(
    $state,
    userService,
    railAmendmentService,
    l10nService,
) {
    'ngInject';

    Object.assign(this, {
        isTripCancelled,
        isTripPaymentTypeInvoice,

        isAmendmentPendingApproval,
        isAmendmentApproved,
        isAmendmentDeclined,

        bookAmendment,

        doesTripContainTrainlineTrain,
        doesTripContainMultipleTrainlineItems,
        doesTripContainBillbackRoom,
        doesTripContainAmadeusItem,
        getTitle,
        getTripItemsCount,
        getTravellersCount,
        getAllTravellers,
        getSeatsCount,
        navigateToTripDetails,
        l10nService,
    });

    function navigateToTripDetails(tripId) {
        $state.go(tripStates.TRIP_DETAILS, {
            id: tripId,
            department: userService.getCurrentDepartment(),
        }, {reload: true});
    }

    function doesTripContainTrainlineTrain(tripDetails) {
        return !!(tripDetails && tripDetails.Rails && tripDetails.Rails.length);
    }

    function isTripPaymentTypeInvoice(tripCheckoutModel) {
        const {PaymentMethods: {invoice}} = paymentsConst;
        return tripCheckoutModel
            && tripCheckoutModel.selectedOption
            && tripCheckoutModel.selectedOption.paymentMethod === invoice;
    }

    function doesTripContainBillbackRoom(tripDetails) {
        const {BillbackPayOnDeparture} = paymentsConst;
        return tripDetails.Hotels
            .some(({PaymentTypeId}) => PaymentTypeId === BillbackPayOnDeparture);
    }

    function doesTripContainAmadeusItem(tripDetails) {
        return tripDetails
            && (
                tripDetails.Hotels && tripDetails.Hotels.length && tripDetails.Hotels.some(amadeusHotel)
                || tripDetails.Flights && tripDetails.Flights.length && tripDetails.Flights.some(amadeusFlight)
                || tripDetails.Cars && !!tripDetails.Cars.length
            );

        function amadeusHotel({Supplier}) {
            return Supplier === suppliers.amadeus.id;
        }

        function amadeusFlight({ServiceOption}) {
            return ServiceOption
                && ServiceOption.Id === suppliers.amadeus.serviceOptions.flights;
        }
    }

    function doesTripContainMultipleTrainlineItems(tripDetails) {
        return tripDetails.Rails.length > 1;
    }

    function getTripItemsCount(tripDetails) {
        if (!tripDetails) {
            return 0;
        }
        const items = [
            tripDetails.Cars,
            tripDetails.Hotels,
            tripDetails.Flights,
            tripDetails.Rails,
            tripDetails.EuRails,
        ];

        return items.reduce(countLength, 0);

        function countLength(sum, value) {
            return sum + (value && value.length || 0);
        }
    }

    function isTripCancelled(tripDetails) {
        return tripDetails.Status === tripStatuses.CANCELLED;
    }

    function isAmendmentDeclined(tripDetails) {
        return tripDetails.CurrentStage === STAGE.AMENDMENT_DECLINED;
    }

    function getTravellersCount(tripDetails) {
        return getAllTravellers(tripDetails.Travelers).length;
    }

    function getSeatsCount(tripDetails) {
        const {Adult = [], Child = []} = tripDetails.Travelers;
        return [...Adult, ...Child].length;
    }

    function getAllTravellers(travellers) {
        const {Adult = [], Child = [], Infant = []} = travellers;
        return [...Adult, ...Child, ...Infant];
    }

    function isAmendmentApproved(tripDetails) {
        return tripDetails.CurrentStage === STAGE.AMENDMENT_APPROVED;
    }

    function isAmendmentPendingApproval(tripDetails) {
        return tripDetails.CurrentStage === STAGE.AMENDMENT_PENDING_APPROVAL;
    }

    function bookAmendment(tripDetails) {
        if (!isAmendmentApproved(tripDetails)) {
            return;
        }
        const newRailRecommendation = tripDetails.Rails.find(({Status}) => Status === tripStatuses.QUOTE);
        if (!newRailRecommendation) {
            return;
        }
        railAmendmentService.navigateToCheckoutWithNewRailGroup(newRailRecommendation.Id, tripDetails.Id);
    }

    function getTitle(tripDetails) { // eslint-disable-line
        const status = tripDetails.Status;
        const stage = tripDetails.CurrentStage;
        const isLocked = tripDetails.Locked;
        const isTimeLocked = tripDetails.IsTimeLocked;

        if (status === tripStatuses.PENDING_CONFIRMATION && tripDetails.IsOfflineTrip) {
            return l10nService.localize(STAGE_LABELS[STAGE.PENDING_CONFIRMATION]);
        }

        if (tripDetails.Locked && status === tripStatuses.HELD && stage === STAGE.NOT_SENT) {
            return `${status}: ${l10nService.localize('with the online helpdesk')}`;
        }

        if (![tripStatuses.QUOTE, tripStatuses.HELD].includes(status)) {
            if (isLocked) {
                return `${l10nService.localize(status)}: ${l10nService.localize('with the online helpdesk')}`;
            }
            if (isTimeLocked) {
                return `${l10nService.localize(status)}${l10nService.localize(': contact online helpdesk')}`;
            }
        }

        if (status === tripStatuses.CONFIRMED && stage === STAGE.APPROVED) {
            return l10nService.localize(status);
        }

        if (stage === STAGE.AMENDMENT_PENDING_APPROVAL) {
            return l10nService.localize(STAGE_LABELS[stage]);
        }

        if (angular.isDefined(STAGE_LABELS[stage])) {
            return [l10nService.localize(status), l10nService.localize(STAGE_LABELS[stage])]
                .filter((value) => !!value)
                .join(': ');
        }

        return l10nService.localize(status);
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/trip/services/trip-details-service.js